import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FleetDriver, FleetVehicle } from '../models/fleet-models';
import {
    assignFleetVehicleDriver,
    createFleetDriverForUser,
    fetchFleetDrivers,
    fetchFleetVehicles,
    linkFleetDriver,
    unlinkFleetDriver,
    showFleetError,
} from '../utils/fleet-hooks';
import { CustomToastContent } from './CustomToastContent';
import { InfoModal } from './InfoModal';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';

interface Props {
    userId: number;
    userName?: string;
}

const NO_VEHICLE = '';

/**
 * Връзката на потребителя с GPS Areon: кой шофьор е там и коя кола кара. Промените се прилагат веднага
 * в GPS Areon със собствените си бутони, не със запазването на формата - смяната на кола важи от
 * момента, в който е направена, и GPS Areon я записва в историята кой какво е карал.
 */
export const FleetUserSection = ({ userId, userName }: Props) => {
    const { t } = useTranslation('translation', { keyPrefix: 'fleet' });
    const [drivers, setDrivers] = useState<FleetDriver[]>([]);
    const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [selectedDriverId, setSelectedDriverId] = useState<string>();
    const [selectedVehicle, setSelectedVehicle] = useState<string>(NO_VEHICLE);
    // Потвърждението е в същия модал като изтриването в таблиците (InfoModal), не в window.confirm.
    const [pendingConfirm, setPendingConfirm] = useState<{ text: string; buttonText: string; action: () => void }>();

    const load = useCallback(async () => {
        setIsLoading(true);
        setLoadError(false);
        try {
            const [driverList, vehicleList] = await Promise.all([
                fetchFleetDrivers(),
                fetchFleetVehicles({ skipErrorInterceptor: true }),
            ]);
            setDrivers(Array.isArray(driverList) ? driverList : []);
            setVehicles(Array.isArray(vehicleList) ? vehicleList : []);
            const current = (Array.isArray(vehicleList) ? vehicleList : []).find((vehicle) => vehicle.driverUserId === userId);
            setSelectedVehicle(current?.deviceIdentifier ?? NO_VEHICLE);
        } catch {
            setLoadError(true);
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        load().then();
    }, [load]);

    const linkedDriver = drivers.find((driver) => driver.userId === userId);
    const freeDrivers = useMemo(() => drivers.filter((driver) => driver.gpsDriverId && !driver.userId), [drivers]);
    const currentVehicle = vehicles.find((vehicle) => vehicle.driverUserId === userId);
    const vehicleToAssign = vehicles.find((vehicle) => vehicle.deviceIdentifier === selectedVehicle);
    const vehicleChanged = (currentVehicle?.deviceIdentifier ?? NO_VEHICLE) !== selectedVehicle;

    const run = async (action: () => Promise<unknown>, successMessage: string) => {
        setIsSaving(true);
        try {
            await action();
            toast.success(<CustomToastContent content={[successMessage]} />, toastOptionsTopRight);
        } catch (error) {
            await showFleetError(error);
        } finally {
            setIsSaving(false);
            await load();
        }
    };

    const linkExisting = () => {
        if (!selectedDriverId) return;
        run(() => linkFleetDriver(userId, selectedDriverId), t('driverLinked')).then();
    };

    const createDriver = () => {
        run(() => createFleetDriverForUser(userId), t('driverCreated')).then();
    };

    const unlink = () => {
        if (currentVehicle) {
            toast.error(<CustomToastContent content={[t('removeVehicleFirst')]} />, toastOptionsTopRight);
            return;
        }
        setPendingConfirm({
            text: t('confirmUnlink', { name: linkedDriver?.name ?? '' }),
            buttonText: 'confirm',
            action: () => run(() => unlinkFleetDriver(userId), t('driverUnlinked')).then(),
        });
    };

    const saveVehicle = () => {
        const otherDriver = vehicleToAssign?.driverUserId && vehicleToAssign.driverUserId !== userId
            ? vehicleToAssign.driverUserName ?? vehicleToAssign.gpsDriverName
            : vehicleToAssign && !vehicleToAssign.driverUserId && vehicleToAssign.gpsDriverName;
        const question = vehicleToAssign
            ? t('confirmAssign', { plate: vehicleToAssign.plateNumber ?? vehicleToAssign.deviceIdentifier })
              + (otherDriver ? ' ' + t('confirmReplaceDriver', { name: otherDriver }) : '')
            : t('confirmRemoveVehicle', { plate: currentVehicle?.plateNumber ?? '' });
        setPendingConfirm({ text: question, buttonText: 'confirm', action: () => run(async () => {
            // Шофьорът е на най-много една кола: първо слиза от сегашната, после се качва на новата.
            if (currentVehicle) {
                await assignFleetVehicleDriver(currentVehicle.deviceIdentifier, null);
            }
            if (vehicleToAssign) {
                await assignFleetVehicleDriver(vehicleToAssign.deviceIdentifier, userId);
            }
        }, t('vehicleSaved')).then() });
    };

    const vehicleOptions = [
        { value: NO_VEHICLE, label: t('noVehicle') },
        ...vehicles.map((vehicle) => ({
            value: vehicle.deviceIdentifier,
            label: [
                vehicle.plateNumber ?? vehicle.deviceIdentifier,
                vehicle.label,
                vehicle.driverUserId === userId
                    ? undefined
                    : vehicle.driverUserName ?? vehicle.gpsDriverName
                        ? `(${t('drivenBy')} ${vehicle.driverUserName ?? vehicle.gpsDriverName})`
                        : undefined,
            ].filter(Boolean).join(' '),
        })),
    ];

    return (
        <div className='w-100 border rounded p-3 mb-3 bg-light'>
            <h6 className='mb-3'>{t('gpsAreon')}</h6>
            <InfoModal
                show={!!pendingConfirm}
                translatedText={pendingConfirm?.text}
                buttonText={pendingConfirm?.buttonText}
                onHide={(confirmed) => {
                    const action = pendingConfirm?.action;
                    setPendingConfirm(undefined);
                    if (confirmed && action) action();
                }}
            />
            {isLoading ? (
                <Spinner animation='border' size='sm' />
            ) : loadError ? (
                <div className='text-danger small'>{t('loadFailed')}</div>
            ) : (
                <>
                    <Form.Group className='mb-3'>
                        <Form.Label className='mb-1'>{t('gpsDriver')}</Form.Label>
                        {linkedDriver ? (
                            <div className='d-flex align-items-center gap-2'>
                                <strong>{linkedDriver.name ?? t('deletedInGps')}</strong>
                                <Button size='sm' variant='outline-danger' type='button' disabled={isSaving} onClick={unlink}>
                                    {t('unlink')}
                                </Button>
                            </div>
                        ) : (
                            <div className='d-flex gap-2 align-items-center flex-wrap'>
                                <div style={{ minWidth: 260 }}>
                                    <Select
                                        value={freeDrivers.filter((driver) => driver.gpsDriverId === selectedDriverId)}
                                        onChange={(driver) => setSelectedDriverId(driver?.gpsDriverId)}
                                        options={freeDrivers}
                                        getOptionLabel={(driver) => driver.name ?? ''}
                                        getOptionValue={(driver) => driver.gpsDriverId ?? ''}
                                        placeholder={t('chooseGpsDriver')}
                                        noOptionsMessage={() => t('noFreeDrivers')}
                                    />
                                </div>
                                <Button size='sm' type='button' disabled={isSaving || !selectedDriverId} onClick={linkExisting}>
                                    {t('link')}
                                </Button>
                                <span className='text-secondary small'>{t('or')}</span>
                                <Button size='sm' variant='outline-primary' type='button' disabled={isSaving} onClick={createDriver}>
                                    {t('createDriver', { name: userName ?? '' })}
                                </Button>
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group>
                        <Form.Label className='mb-1'>{t('vehicle')}</Form.Label>
                        <div className='d-flex gap-2 align-items-center flex-wrap'>
                            <div style={{ minWidth: 320 }}>
                                <Select
                                    value={vehicleOptions.find((option) => option.value === selectedVehicle)}
                                    onChange={(option) => setSelectedVehicle(option?.value ?? NO_VEHICLE)}
                                    options={vehicleOptions}
                                    isDisabled={!linkedDriver || isSaving}
                                />
                            </div>
                            <Button size='sm' type='button' disabled={!linkedDriver || !vehicleChanged || isSaving} onClick={saveVehicle}>
                                {isSaving ? <Spinner animation='border' size='sm' /> : t('saveVehicle')}
                            </Button>
                        </div>
                        <div className='hint mt-1'>{linkedDriver ? t('vehicleHint') : t('linkFirst')}</div>
                    </Form.Group>
                </>
            )}
        </div>
    );
};
